/**
 * State for add appointment module
 * Author: Tirthamouli Baidya
 */
export const state = {
  /**
   * Check if there is an overlap -
   * NOT_CHECKED - Not checked if there is an over lap (default)
   * ASK_USER - Need to ask user - Watch this in component to show popup
   * ALLOWED - User has allowed to add overlap
   */
  overLapCheck: "NOT_CHECKED",

  /**
   * Show loading spinner when this is true and show main page when this is false
   */
  loadingAddAppointment: false,

  /**
   * Edit appointment or Add appointment
   * If edit also set edit id
   */
  edit: {
    isEdit: false,
    editMode: false,
    editLevel: 0,
    firstClick: false,
    id: 0,
    oneAllFuture: "single",
    type: "",
    selectedIndex: -1,
    otherType: "",
    usePsybooksVideo: false
  },

  /**
   * AppointmentSent to confirm that appointment has been sent
   */
  appointmentSent: false,

  /**
   * Is the appointment all day
   */
  allDay: false,

  /**
   * Appointment date
   */
  date: {
    dayOfMonth: 0,
    month: 0,
    year: 0
  },

  /**
   * Appointment start time
   */
  startTime: {
    hr: 10,
    min: 0,
    sec: 0
  },

  /**
   * Appointment end time
   */
  endTime: {
    hr: 11,
    min: 0,
    sec: 0,
    incrementBy: {
      hr: 0,
      min: 45,
      sec: 0
    }
  },

  /**
   * Appointment type and which is selected
   */
  appointmentType: {
    fetched: false,
    /**
     * The clients with provider id as key
     *
     * Format
     * {
     *   [provider_id]: {
     *      id:3310, // The person id
     *      user_id:3310 // If the person is also a portal user, then this will be set to the person id
     *      is_video:null, // For groups, if this is true, then it is a video group
     *      favourite_id:280, // The favourite id if the client is a favourite
     *      name:"1 ray, 1", // Name of the client
     *      phone: String[4], // Phone string array
     *      provider:3159, // The provider id
     *      selected_phone:null, // The selected phone number
     *      status:"1", // The satus, Active(1), Archived(0)
     *      type:"person", // Client is always a person
     *   }[]
     * }
     */
    clients: {},

    /**
     * The other_event with provider id as key
     *
     * Format
     * {
     *    [provider_id]: {
     *      id:3310, // The other_event id
     *      user_id:3310 // If the person is also a portal user, then this will be set to the person id
     *      is_video:null, // For groups, if this is true, then it is a video group
     *      favourite_id:280, // The favourite id if the client is a favourite
     *      name:"1 ray, 1", // Name of the client
     *      phone: String[4], // Phone string array
     *      provider:3159, // The provider id
     *      selected_phone:null, // The selected phone number
     *      status: null, // The satus is always null
     *      type:"other_event", // Client is always a other_event
     *   }[]
     * }
     */
    people: {},

    /**
     * The event with the provider id as key
     *
     * Format
     * {
     *    [provider_id]: {
     *      id:3310, // The event id
     *      user_id: 0 // User id is alwasys 0 for event
     *      is_video:null, // For groups, if this is true, then it is a video group
     *      favourite_id: 0, // The favourite id is always 0 for events
     *      name:"1 ray, 1", // Name of the event
     *      phone: String[4], // Phone string array - This will always be empty
     *      provider:3159, // The provider id
     *      selected_phone:null, // The selected phone number is always null
     *      status: null, // The satus is always null
     *      type:"event_appointment", // Event is always a event_appointment
     *   }[]
     * }
     */
    events: {},

    /**
     * The groups with the provider id as key
     *
     * Format
     * {
     *    [provider_id]: {
     *      id:3310, // The group id
     *      user_id: 0 // User id is alwasys 0
     *      is_video:null, // For groups, if this is 1, then it is a video group
     *      favourite_id: 0, // The favourite id is always 0 for groups
     *      name:"1 ray, 1", // Name of the group
     *      phone: String[4], // Phone string array - This will always be empty
     *      provider:3159, // The provider id
     *      selected_phone:null, // The selected phone number is always null
     *      status: null, // The satus is always null
     *      type:"group_appointment", // Group is always a group_appointment
     *   }[]
     * }
     */
    groups: {},

    /**
     * The clients with provider id as key
     *
     * Format
     * {
     *   [provider_id]: {
     *      id:3310, // The person id
     *      user_id:3310 // If the person is also a portal user, then this will be set to the person id
     *      is_video:null, // For groups, if this is true, then it is a video group
     *      favourite_id:280, // The favourite id if the client is a favourite
     *      name:"1 ray, 1", // Name of the client
     *      phone: String[4], // Phone string array
     *      provider:3159, // The provider id
     *      selected_phone:null, // The selected phone number
     *      status:"1", // The satus, Active(1), Archived(0)
     *      type:"app_user", // App user will always have the type "app_user"
     *   }[]
     * }
     */
    appUsers: {},

    /**
     * The clients with provider id as key
     *
     * Format
     * {
     *    id:3310, // The person id
     *    user_id:3310 // This will always be the same as id
     *    is_video:null, // For groups, if this is true, then it is a video group
     *    favourite_id:280, // The favourite id if the client is a favourite
     *    name:"1 ray, 1", // Name of the client
     *    phone: String[4], // Phone string array
     *    provider:3159, // The provider id
     *    selected_phone:null, // The selected phone number
     *    status:"1", // The satus, Active(1), Archived(0)
     *    type:"subscriber", // Subscriber will always have the type "subscriber"
     * }
     */
    subscribers: [],
    selectedId: 0,
    selectedType: "",
    clear: true,
    removeOnRefresh: {
      providerId: 0,
      type: "",
      id: -1
    }
  },

  /**
   * Repeat options and which one is selected
   */
  repeat: {
    /**
     * Order of the repeat
     */
    order: [
      { innerOrOuter: "outer", index: 0 },
      { innerOrOuter: "inner", index: 0 },
      { innerOrOuter: "outer", index: 1 },
      { innerOrOuter: "outer", index: 2 },
      { innerOrOuter: "outer", index: 3 },
      { innerOrOuter: "inner", index: 1 },
      { innerOrOuter: "outer", index: 4 },
      { innerOrOuter: "inner", index: 2 },
      { innerOrOuter: "inner", index: 3 }
    ],
    /**
     * Will have inner options
     */
    innerOptions: ["Daily", "Weekly", "Monthly", "Yearly"],
    /**
     * Will not have inner options
     */
    outerOptions: [
      "Never",
      "Every weekday (Mon-Fri)",
      "Every Mon, Wed and Fri",
      "Every Tue and Thu",
      "Every other week"
    ],
    /**
     * Weater or outer is selected
     */
    innerOrOuter: "outer",
    /**
     * Seleted index of the repeat
     */
    selectedIndex: 0,
    /**
     * Custom value
     */
    custom: 1,
    /**
     * Which week days have weekly appointment
     */
    weeklyOn: {
      0: false,
      1: false,
      2: false,
      3: false,
      4: false,
      5: false,
      6: false
    },
    /**
     * Incase of month type, the full type is stored here
     */
    fullMonthlyType: ""
  },

  /**
   * Start repeat date
   */
  startRepeat: {
    dayOfMonth: 0,
    month: 0,
    year: 0
  },

  /**
   * End repeat date
   */
  endRepeat: {
    dayOfMonth: 0,
    month: 0,
    year: 0,
    never: true
  },

  /**
   * Office location and which one is selected
   */
  officeLocation: {
    custom: false,
    customValue: "",
    /**
     * Format
     * {
     *   [provider_id]: {
     *      address_id: 1479,  // The address ID
     *      city: "efefe", // The city
     *      line1: "wdefe", // Line 1 of address
     *      line2: "", // Line 2 of address
     *      postal_code: "75101", // The postal code
     *      provider: 3159, // Provider id
     *      provider_address_id: 756, // Provider address id
     *      state: "AZ", // State,
     *      status: 1, // Status
     *   }[]
     * }
     */
    locationDetails: {},
    selectedIndex: 0
  },

  /**
   * Selected provider
   */
  provider: {
    selectedIndex: 0,
    list: []
  },

  /**
   * Is this a video appointment
   */
  isOffice: true,
  isVideo: false,
  isPhone: false,
  isOther: false,

  /**
   * Notes
   */
  notes: "",

  /**
   * Weather to use psybooks video
   */
  usePsybooksVideo: false,

  /**
   * Weather to use psybooks connect video
   */
  useConnectVideo: false,

  /**
   * Reminder for secure vide
   */
  videoReminder: "N",

  /**
   * Weather to show second level header
   * Not used anymore
   */
  secondLevelHeaderActive: false,

  /**
   * Hide back and add buttons
   */
  hideBackAndAddButton: false
};
